import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, Loader2, CheckCircle2, Calendar as CalendarIcon, Pill, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = [
  { key: 'energy', label: 'Energy', emoji: '⚡' },
  { key: 'sleep_quality', label: 'Sleep', emoji: '🌙' },
  { key: 'mood', label: 'Mood', emoji: '😊' },
  { key: 'digestion', label: 'Digestion', emoji: '🍃' },
  { key: 'focus', label: 'Focus', emoji: '🎯' },
];

const DEFAULT_SCORES = { energy: 3, sleep_quality: 3, mood: 3, digestion: 3, focus: 3 };

function formatLongDate(yyyymmdd) {
  // Render YYYY-MM-DD as a human label without timezone surprises.
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

export default function BackfillDayModal({
  open,
  date,            // YYYY-MM-DD
  onClose,
  onSaved,         // (date) => void  — called after a successful save so parent can refresh
  getAuthHeader,
}) {
  const [loading, setLoading] = useState(true);
  const [selectedSupps, setSelectedSupps] = useState(new Set());
  const [supplementList, setSupplementList] = useState([]);
  const [scores, setScores] = useState(DEFAULT_SCORES);
  const [notes, setNotes] = useState('');
  const [wellnessLogged, setWellnessLogged] = useState(false);
  const [savingSupps, setSavingSupps] = useState(false);
  const [savingWellness, setSavingWellness] = useState(false);

  const fetchDay = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const [checkRes, wellnessRes, remindersRes] = await Promise.all([
        axios.get(`${API}/checkin/day/${date}`, { headers: getAuthHeader() }),
        axios.get(`${API}/wellness/day/${date}`, { headers: getAuthHeader() }),
        axios.get(`${API}/reminders`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
      ]);
      setSelectedSupps(new Set(checkRes.data.supplements_taken || []));

      // Build deduped supplement list from active reminders (drop disabled, Rx, water).
      const reminders = Array.isArray(remindersRes.data) ? remindersRes.data : [];
      const seen = new Set();
      const names = [];
      reminders.forEach(r => {
        if (r.enabled === false) return;
        if (r.is_medication) return;
        const raw = (r.supplement_name || '').trim();
        if (!raw) return;
        if (raw.startsWith('💧') || /water \(cup/i.test(raw)) return;
        const key = raw.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        names.push(raw);
      });
      // Also include any supplements that were already logged on this day but
      // aren't in active reminders (e.g. user disabled the reminder afterwards),
      // so the user can still see/uncheck them.
      (checkRes.data.supplements_taken || []).forEach(s => {
        if (!seen.has(s.toLowerCase())) {
          names.push(s);
          seen.add(s.toLowerCase());
        }
      });
      setSupplementList(names);

      const w = wellnessRes.data;
      setWellnessLogged(!!w.logged);
      if (w.logged && w.scores) {
        setScores({ ...DEFAULT_SCORES, ...w.scores });
        setNotes(w.notes || '');
      } else {
        setScores(DEFAULT_SCORES);
        setNotes('');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to load this day';
      toast.error(msg);
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [date, getAuthHeader, onClose]);

  useEffect(() => {
    if (open) fetchDay();
  }, [open, fetchDay]);

  const toggleSupp = (name) => {
    setSelectedSupps(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const saveSupplements = async () => {
    setSavingSupps(true);
    try {
      await axios.post(`${API}/checkin`, {
        supplements_taken: Array.from(selectedSupps),
        date,
      }, { headers: getAuthHeader() });
      toast.success('Supplements saved for ' + formatLongDate(date));
      onSaved?.(date);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save';
      toast.error(msg);
    } finally {
      setSavingSupps(false);
    }
  };

  const saveWellness = async () => {
    setSavingWellness(true);
    try {
      await axios.post(`${API}/wellness/log`, {
        ...scores,
        notes,
        date,
      }, { headers: getAuthHeader() });
      setWellnessLogged(true);
      toast.success('Wellness saved for ' + formatLongDate(date));
      onSaved?.(date);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save';
      toast.error(msg);
    } finally {
      setSavingWellness(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
          onClick={onClose}
          data-testid="backfill-modal-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid="backfill-modal"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-border px-5 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CalendarIcon className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-[#1A1A1A]" data-testid="backfill-modal-title">
                    {date ? formatLongDate(date) : ''}
                  </h3>
                  <p className="text-xs text-muted-foreground">Backfill log for this day</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                data-testid="backfill-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {loading ? (
              <div className="p-10 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="p-5 space-y-6">
                {/* Supplements section */}
                <section data-testid="backfill-supplements-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Pill className="w-4 h-4 text-emerald-600" />
                    <h4 className="font-semibold text-sm text-[#1A1A1A]">Supplements taken</h4>
                  </div>
                  {supplementList && supplementList.length > 0 ? (
                    <>
                      {/* One-tap controls */}
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => setSelectedSupps(new Set(supplementList))}
                          className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5"
                          data-testid="backfill-took-everything-btn"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          I took everything
                        </button>
                        {selectedSupps.size > 0 && (
                          <button
                            type="button"
                            onClick={() => setSelectedSupps(new Set())}
                            className="px-3 py-2 rounded-lg bg-muted text-muted-foreground text-xs font-medium hover:bg-muted/70 transition-colors"
                            data-testid="backfill-clear-all-btn"
                          >
                            Clear
                          </button>
                        )}
                      </div>

                      <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                        {supplementList.map(name => {
                          const checked = selectedSupps.has(name);
                          return (
                            <label
                              key={name}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                                checked ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-border hover:bg-muted'
                              }`}
                              data-testid={`backfill-supp-${name.replace(/\s/g, '-').toLowerCase()}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSupp(name)}
                                className="w-4 h-4 accent-emerald-600"
                              />
                              <span className="text-sm flex-1">{name}</span>
                              {checked && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                            </label>
                          );
                        })}
                      </div>
                      <Button
                        onClick={saveSupplements}
                        disabled={savingSupps}
                        className="w-full mt-3"
                        data-testid="backfill-save-supplements-btn"
                      >
                        {savingSupps ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save Supplements ({selectedSupps.size})
                      </Button>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      No supplements in your active reminders. Add some on the Reminders page first.
                    </p>
                  )}
                </section>

                <div className="h-px bg-border" />

                {/* Wellness section */}
                <section data-testid="backfill-wellness-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Heart className="w-4 h-4 text-violet-600" />
                    <h4 className="font-semibold text-sm text-[#1A1A1A]">
                      How were you feeling?
                      {wellnessLogged && (
                        <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                          Already logged
                        </span>
                      )}
                    </h4>
                  </div>

                  <div className="space-y-3">
                    {METRICS.map(m => (
                      <div key={m.key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-[#1A1A1A]">{m.emoji} {m.label}</span>
                          <span className="text-xs font-bold text-primary">{scores[m.key]}/5</span>
                        </div>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(val => (
                            <button
                              key={val}
                              onClick={() => setScores(prev => ({ ...prev, [m.key]: val }))}
                              className={`flex-1 h-7 rounded-md text-xs font-medium transition-all ${
                                scores[m.key] >= val
                                  ? 'bg-primary text-white'
                                  : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                              }`}
                              data-testid={`backfill-score-${m.key}-${val}`}
                            >
                              {val}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div>
                      <label className="text-xs font-medium text-[#1A1A1A] block mb-1">
                        Notes (optional)
                      </label>
                      <Textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="Anything notable about this day?"
                        rows={2}
                        className="resize-none text-sm"
                        data-testid="backfill-notes-input"
                      />
                    </div>

                    <Button
                      onClick={saveWellness}
                      disabled={savingWellness}
                      variant="secondary"
                      className="w-full"
                      data-testid="backfill-save-wellness-btn"
                    >
                      {savingWellness ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Save Wellness
                    </Button>
                  </div>
                </section>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
